import React from 'react';
import { Card, CardBody, CardHeader, Badge } from 'reactstrap';
import ReactApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { MapPin, TrendingUp, Award } from 'react-feather';

interface BranchDistributionProps {
    data: Array<{ branch_name: string; count: number }>;
}

export default function BranchDistributionChart({ data }: BranchDistributionProps) {
    const totalVehicles = data.reduce((sum, item) => sum + item.count, 0);
    const topBranch = data.length > 0 ? data[0] : null;
    const avgVehicles = totalVehicles / data.length;

    // Colorful gradient palette for bars
    const colorPalette = [
        '#667eea', '#f093fb', '#4facfe', '#43e97b', '#fa709a',
        '#fee140', '#30cfd0', '#a8edea', '#ff6b6b', '#c471ed'
    ];

    const options: ApexOptions = {
        chart: {
            type: 'bar',
            height: 400,
            toolbar: {
                show: false,
            },
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 8,
                borderRadiusApplication: 'end',
                dataLabels: {
                    position: 'top',
                },
                distributed: true, // Each bar gets a different color
            },
        },
        dataLabels: {
            enabled: true,
            offsetX: 35,
            style: {
                fontSize: '13px',
                fontWeight: 700,
                colors: ['#2d3748'],
            },
            formatter: (val: number) => {
                return val.toString();
            },
        },
        colors: colorPalette,
        xaxis: {
            categories: data.map(item => item.branch_name),
            labels: {
                style: {
                    fontSize: '12px',
                    fontWeight: 600,
                    colors: '#718096',
                },
            },
            axisBorder: {
                show: false,
            },
        },
        yaxis: {
            labels: {
                style: {
                    fontSize: '12px',
                    fontWeight: 600,
                    colors: '#2d3748',
                },
                maxWidth: 150,
            },
        },
        grid: {
            borderColor: '#e2e8f0',
            strokeDashArray: 4,
            xaxis: {
                lines: {
                    show: true,
                },
            },
            yaxis: {
                lines: {
                    show: false,
                },
            },
            padding: {
                top: 0,
                right: 20,
                bottom: 0,
                left: 10,
            },
        },
        tooltip: {
            enabled: true,
            theme: 'light',
            y: {
                formatter: (value) => {
                    const percentage = ((value / totalVehicles) * 100).toFixed(1);
                    return `${value} vehicles (${percentage}%)`;
                },
            },
            style: {
                fontSize: '14px',
            },
        },
        legend: {
            show: false,
        },
        states: {
            hover: {
                filter: {
                    type: 'darken',
                    value: 0.15,
                },
            },
        },
    };

    const series = [
        {
            name: 'Vehicles',
            data: data.map(item => item.count),
        },
    ];

    return (
        <Card className="chart-card" style={{ height: '100%' }}>
            <CardHeader className="pb-0">
                <div className="d-flex align-items-start justify-content-between">
                    <div className="flex-grow-1">
                        <h5 className="mb-1">Branch Distribution</h5>
                        <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
                            Top 10 branches by vehicle count
                        </p>
                    </div>
                    <div
                        className="stat-card-icon"
                        style={{
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            boxShadow: '0 4px 14px 0 rgba(240, 147, 251, 0.4)',
                            width: '48px',
                            height: '48px',
                        }}
                    >
                        <MapPin size={22} color="white" strokeWidth={2.5} />
                    </div>
                </div>

                {/* Summary Stats */}
                <div className="row mt-3 g-2">
                    <div className="col-4">
                        <div
                            className="p-2 rounded text-center"
                            style={{
                                background: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                border: '1px solid #667eea30'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-center mb-1">
                                <TrendingUp size={14} className="text-primary me-1" />
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Total</small>
                            </div>
                            <strong style={{ fontSize: '1rem', color: '#667eea' }}>
                                {totalVehicles}
                            </strong>
                        </div>
                    </div>
                    <div className="col-4">
                        <div
                            className="p-2 rounded text-center"
                            style={{
                                background: 'linear-gradient(135deg, #10b98115 0%, #05966915 100%)',
                                border: '1px solid #10b98130'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-center mb-1">
                                <Award size={14} className="text-success me-1" />
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Top</small>
                            </div>
                            <strong style={{ fontSize: '1rem', color: '#10b981' }}>
                                {topBranch?.count || 0}
                            </strong>
                        </div>
                    </div>
                    <div className="col-4">
                        <div
                            className="p-2 rounded text-center"
                            style={{
                                background: 'linear-gradient(135deg, #f59e0b15 0%, #d9770615 100%)',
                                border: '1px solid #f59e0b30'
                            }}
                        >
                            <div className="d-flex align-items-center justify-content-center mb-1">
                                <MapPin size={14} className="text-warning me-1" />
                                <small className="text-muted" style={{ fontSize: '0.7rem' }}>Avg</small>
                            </div>
                            <strong style={{ fontSize: '1rem', color: '#f59e0b' }}>
                                {Math.round(avgVehicles)}
                            </strong>
                        </div>
                    </div>
                </div>

                {/* Top Branch Badge */}
                {topBranch && (
                    <div className="mt-3">
                        <Badge
                            color="primary"
                            className="w-100 py-2"
                            style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                fontSize: '0.8125rem',
                                fontWeight: 600
                            }}
                        >
                            <Award size={14} className="me-1" />
                            Leading Branch: {topBranch.branch_name} ({topBranch.count} vehicles)
                        </Badge>
                    </div>
                )}
            </CardHeader>
            <CardBody>
                <ReactApexChart options={options} series={series} type="bar" height={400} />
            </CardBody>
        </Card>
    );
}

